import './style.scss';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import JsPDF from 'jspdf';
import 'jspdf-autotable';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
  subMonths
} from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'react-toastify';

import {
  createCalendarEvent,
  getCalendarEvents,
  getSchools,
  updateCalendarEvent
} from '../../API/apiService';
import ModalContainer from '../../components/modalContainer';
import AdminProcessScopeFilter from '../../components/adminProcessScopeFilter';
import CustomFilterSelect from '../../shared/customFilterSelect';
import { constants } from '../../config/constants';
import {
  buildScopedSchools,
  normalizeAdminScope
} from '../../utils/adminScope';
import { isAdminRole, isReviewerRole } from '../../utils/session';
import { ENTITY_TYPE_FILTER_OPTIONS, matchesEntityType } from '../../utils/entityType';
import { getMultiFilterLabel, matchesAnySelected, normalizeMultiSelection } from '../../utils/multiFilter';
import {
  getEncounterGroupValidationMessage,
  GROUPS_MUST_BE_CREATED_MESSAGE,
  requiresEncounterGroup
} from './calendarRules';
import {
  getCalendarWeekDays,
  getDownloadableAgendaEvents,
  getWeeklyAgendaEvents,
  layoutOverlappingEvents
} from './calendarExport';

const WEEK_DAYS = ['Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab', 'Dom'];
const COLOMBIA_OFFSET = '-05:00';
const LATE_SCHEDULING_MINIMUM_DAYS = 2;
const STATUS_OPTIONS = [
  { value: 'programado', label: 'Programado' },
  { value: 'cancelado', label: 'Cancelado' },
  { value: 'realizado', label: 'Realizado' },
  { value: 'reprogramado', label: 'Reprogramado' }
];
const DEFAULT_ENCOUNTER_OPTIONS = [
  { value: 'concertacion', label: 'Concertaci\u00f3n' },
  { value: 'socializacion', label: 'Socializaci\u00f3n' },
  { value: 'encuentro_1', label: 'Encuentro 1' },
  { value: 'encuentro_2', label: 'Encuentro 2' },
  { value: 'encuentro_3', label: 'Encuentro 3' },
  { value: 'encuentro_4', label: 'Encuentro 4' },
  { value: 'encuentro_5', label: 'Encuentro 5' },
  { value: 'encuentro_6', label: 'Encuentro 6' },
  { value: 'encuentro_7', label: 'Encuentro 7' },
  { value: 'encuentro_8', label: 'Encuentro 8' }
];
const COP_ENCOUNTER_OPTIONS = [
  { value: 'solicitud', label: 'Solicitud' },
  { value: 'planeacion', label: 'Planeacion' },
  { value: 'desarrollo', label: 'Desarrollo' },
  { value: 'cierre', label: 'Cierre' }
];
const INCIDENCE_ACTION_OPTION = {
  value: 'accion_incidencia',
  label: 'Acci\u00f3n de incidencia'
};
const ENCOUNTER_OPTIONS = [
  ...DEFAULT_ENCOUNTER_OPTIONS,
  ...COP_ENCOUNTER_OPTIONS,
  INCIDENCE_ACTION_OPTION
];
const AGENDA_EXPORT_HEADERS = [
  'Proceso',
  'Fecha',
  'Hora de inicio',
  'Hora estimada de finalización',
  'Fecha de carga',
  'Estado',
  'Entidad Aliada',
  'Codigo Entidad Aliada',
  'Comuna',
  'Direccion',
  '# de encuentro',
  'Mediador',
  'Detalles'
];
const WEEKLY_PDF_COLORS = [
  { border: [236, 72, 91], fill: [255, 235, 239] },
  { border: [20, 174, 136], fill: [229, 248, 242] },
  { border: [0, 174, 239], fill: [229, 247, 253] },
  { border: [132, 86, 245], fill: [239, 234, 255] },
  { border: [245, 141, 37], fill: [255, 241, 224] },
  { border: [228, 46, 142], fill: [255, 232, 245] },
  { border: [70, 104, 230], fill: [234, 239, 255] },
  { border: [112, 173, 36], fill: [239, 248, 226] }
];

function toInputDate(date) {
  if (!date) return '';
  return format(date, 'yyyy-MM-dd');
}

function toInputTime(date) {
  if (!date) return '';
  return format(date, 'HH:mm');
}

function normalizeDateInput(value, boundary = 'start') {
  if (!value) return null;

  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return null;

  if (boundary === 'end') {
    date.setHours(23, 59, 59, 999);
  }

  return date;
}

function getEventDate(event) {
  const date = new Date(event?.startsAt);
  return Number.isNaN(date.getTime()) ? null : date;
}

function getEventEndDate(event) {
  if (!event?.endsAt) return null;
  const date = new Date(event?.endsAt);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatBogotaDateTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const parts = new Intl.DateTimeFormat('es-CO', {
    timeZone: 'America/Bogota',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23'
  }).formatToParts(date);
  const part = type => parts.find(item => item.type === type)?.value || '';

  return `${part('day')}/${part('month')}/${part('year')} a las ${part('hour')}:${part('minute')}`;
}

function getBogotaDateKey(value = new Date()) {
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(value || ''))) return String(value);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Bogota',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(date);
  const part = type => parts.find(item => item.type === type)?.value || '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

function getCalendarDayDifference(fromDateKey, toDateKey) {
  if (!fromDateKey || !toDateKey) return null;
  const from = new Date(`${fromDateKey}T00:00:00Z`);
  const to = new Date(`${toDateKey}T00:00:00Z`);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;
  return Math.round((to.getTime() - from.getTime()) / 86400000);
}

function isScheduledWithShortNotice(event) {
  const loadedDate = getBogotaDateKey(event?.createdAt);
  const scheduledDate = getBogotaDateKey(event?.startsAt);
  const difference = getCalendarDayDifference(loadedDate, scheduledDate);
  return difference !== null && difference < LATE_SCHEDULING_MINIMUM_DAYS;
}

function requiresShortNoticeWarning(eventDate) {
  const difference = getCalendarDayDifference(getBogotaDateKey(), eventDate);
  return difference !== null && difference < LATE_SCHEDULING_MINIMUM_DAYS;
}

function isTodayOrPastInBogota(eventDate) {
  const difference = getCalendarDayDifference(getBogotaDateKey(), eventDate);
  return difference !== null && difference <= 0;
}

function getMinimumScheduleDate() {
  const today = new Date(`${getBogotaDateKey()}T00:00:00Z`);
  today.setUTCDate(today.getUTCDate() + 1);
  return today.toISOString().slice(0, 10);
}

function confirmShortNoticeSchedule(eventDate) {
  if (!requiresShortNoticeWarning(eventDate)) return true;
  return window.confirm(
    'Advertencia: los eventos se deben cargar con mínimo 2 días de anticipación. ' +
    'Este evento quedará identificado con un símbolo amarillo de precaución. ¿Deseas continuar?'
  );
}

function getScopeFromRole(role) {
  const normalizedRole = String(role || '').trim().toLowerCase();

  if (normalizedRole === 'mediador cop') return 'cop';
  if (normalizedRole.includes('ndn')) return 'ndn';
  return 'pf';
}

function getScopeLabel(scope) {
  if (scope === 'general') return 'Escuelas de Participacion + Nada de Nervios';
  if (scope === 'cop') return 'COP';
  if (scope === 'ndn') return 'Nada de Nervios';
  if (scope === 'pf') return 'Escuelas de Participacion';
  if (scope === 'diplom') return 'Diplomados';
  return 'Vista general';
}

function isCopScope(scope) {
  return scope === 'cop';
}

function getEncounterOptions(scope) {
  const scopeOptions = isCopScope(scope) ? COP_ENCOUNTER_OPTIONS : DEFAULT_ENCOUNTER_OPTIONS;
  return [...scopeOptions, INCIDENCE_ACTION_OPTION];
}

function getSchoolName(school) {
  return school?.name || school?.address || school?.code || `Entidad Aliada ${school?.id || ''}`;
}

function getSchoolOptionLabel(school) {
  const name = getSchoolName(school);
  return school?.active === false ? `${name} (Pendiente de verificación)` : name;
}

function getSchoolMunicipalityId(school) {
  return school?.municipalityId ?? school?.municipality_id ?? school?.municipality?.id ?? '';
}

function getSchoolMunicipalityName(school) {
  return school?.municipality?.name || school?.municipalityName || '';
}

function getSchoolAddress(school) {
  return String(
    school?.address ??
    school?.locationAddress ??
    school?.location_address ??
    ''
  ).trim();
}

function getSchoolMediatorId(school) {
  return school?.userId ?? school?.user_id ?? school?.user?.id ?? '';
}

function getSchoolMediatorName(school) {
  return (
    school?.user?.fullName ||
    school?.user?.full_name ||
    school?.mediatorName ||
    school?.mediator_name ||
    ''
  );
}

function getGroupName(group) {
  return String(
    group?.name ??
    group?.code ??
    group?.groupName ??
    group?.group_name ??
    `Grupo ${group?.id || ''}`
  ).trim();
}

function getGroupMediatorId(group) {
  return group?.userId ?? group?.user_id ?? group?.user?.id ?? '';
}

function appendGroupToDetails(details, groupName) {
  const normalizedDetails = String(details || '').trim();
  const groupDetail = `Se asiste al grupo ${groupName}`;
  return normalizedDetails
    ? `${normalizedDetails}\n-------\n${groupDetail}`
    : groupDetail;
}

function schoolBelongsToMediator(school, mediatorId) {
  if (!mediatorId) return false;
  if (String(getSchoolMediatorId(school)) === String(mediatorId)) return true;
  return (school?.groups || []).some(group =>
    String(group?.userId ?? group?.user_id ?? group?.user?.id ?? '') === String(mediatorId)
  );
}

function getEncounterLabel(event) {
  const encounterType = String(event?.encounterType || '').trim();
  const option = ENCOUNTER_OPTIONS.find((item) => item.value === encounterType);
  if (option) return option.label;

  if (event?.encounterLabel) return event.encounterLabel;

  return event?.encounterNumber ? `Encuentro ${event.encounterNumber}` : 'Encuentro';
}

function getStatusLabel(status) {
  return STATUS_OPTIONS.find((item) => item.value === status)?.label || 'Programado';
}

function getEventTimeLabel(event) {
  const eventDate = getEventDate(event);
  return eventDate ? format(eventDate, 'hh:mm a') : '--:--';
}

function getEventEndTimeLabel(event) {
  const eventDate = getEventEndDate(event);
  return eventDate ? format(eventDate, 'hh:mm a') : 'Sin hora estimada';
}

function normalizeFilePart(value) {
  const normalized = String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  return normalized || 'agenda';
}

function buildAgendaRows(agendaEvents, scopeLabel) {
  return agendaEvents.map((event) => {
    const eventDate = getEventDate(event);
    const eventEndDate = getEventEndDate(event);

    return {
      Proceso:
        scopeLabel === getScopeLabel('general')
          ? getScopeLabel(event?.scope)
          : scopeLabel,
      Fecha: eventDate ? format(eventDate, 'dd/MM/yyyy') : '',
      'Hora de inicio': eventDate ? format(eventDate, 'HH:mm') : '',
      'Hora estimada de finalización': eventEndDate ? format(eventEndDate, 'HH:mm') : '',
      'Fecha de carga': formatBogotaDateTime(event.createdAt),
      Estado: getStatusLabel(event.status),
      'Entidad Aliada': event.schoolName || '',
      'Codigo Entidad Aliada': event.schoolCode || '',
      Comuna: event.municipalityName || '',
      Direccion: event.locationAddress || '',
      '# de encuentro': getEncounterLabel(event),
      Mediador: event.mediatorName || '',
      Detalles: event.details || ''
    };
  });
}

function downloadWorkbook(workbook, fileName) {
  const workbookOutput = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array'
  });
  const blob = new Blob([workbookOutput], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

function buildAgendaFileBaseName(scopeLabel, hasActiveFilters, visibleMonth) {
  const fileSuffix = hasActiveFilters
    ? `filtrada_${format(new Date(), 'yyyyMMdd_HHmm')}`
    : format(visibleMonth, 'yyyy_MM');

  return `agenda_${normalizeFilePart(scopeLabel)}_${fileSuffix}`;
}

function formatWeekRange(weekValue) {
  const weekDays = getCalendarWeekDays(weekValue);
  if (weekDays.length === 0) return '';
  return `${format(weekDays[0], "d 'de' MMMM", { locale: es })} al ${format(
    weekDays[6],
    "d 'de' MMMM 'de' yyyy",
    { locale: es }
  )}`;
}

function createWeeklyAgendaPdf(weeklyEvents, weekValue, scopeLabel, hasActiveFilters) {
  const weekDays = getCalendarWeekDays(weekValue);
  if (weekDays.length === 0) return;

  const document = new JsPDF({orientation: 'landscape', unit: 'pt', format: 'a3'});
  const pageWidth = document.internal.pageSize.getWidth();
  const pageHeight = document.internal.pageSize.getHeight();
  const margin = 26;
  const timeColumnWidth = 38;
  const gridLeft = margin + timeColumnWidth;
  const gridRight = pageWidth - margin;
  const dayWidth = (gridRight - gridLeft) / weekDays.length;
  const headerBottom = 56;
  const dayHeaderTop = 65;
  const gridTop = 92;

  const mediatorMap = new Map();
  weeklyEvents.forEach(event => {
    const key = String(event?.mediatorId || event?.mediatorName || 'sin-mediador');
    if (!mediatorMap.has(key)) {
      mediatorMap.set(key, {
        name: event?.mediatorName || 'Sin mediador',
        color: WEEKLY_PDF_COLORS[mediatorMap.size % WEEKLY_PDF_COLORS.length]
      });
    }
  });
  const mediators = [...mediatorMap.values()];
  const legendColumns = 4;
  const legendRows = Math.ceil(mediators.length / legendColumns);
  const legendHeight = mediators.length > 0 ? 22 + legendRows * 14 : 20;
  const gridBottom = pageHeight - margin - legendHeight;

  const layoutsByDay = weekDays.map(day => layoutOverlappingEvents(
    weeklyEvents.filter(event => {
      const eventDate = getEventDate(event);
      return eventDate ? isSameDay(eventDate, day) : false;
    })
  ));
  const positionedEvents = layoutsByDay.flat();
  const earliestMinutes = positionedEvents.length > 0
    ? Math.min(...positionedEvents.map(item => item.startMinutes))
    : 6 * 60;
  const latestMinutes = positionedEvents.length > 0
    ? Math.max(...positionedEvents.map(item => item.endMinutes))
    : 21 * 60;
  const startHour = Math.max(0, Math.min(6, Math.floor(earliestMinutes / 60)));
  const endHour = Math.min(24, Math.max(21, Math.ceil(latestMinutes / 60)));
  const totalMinutes = Math.max((endHour - startHour) * 60, 60);
  const gridHeight = gridBottom - gridTop;

  document.setFillColor(0, 40, 88);
  document.rect(0, 0, pageWidth, headerBottom, 'F');
  document.setTextColor(255, 255, 255);
  document.setFont('helvetica', 'bold');
  document.setFontSize(18);
  document.text('Agenda semanal', margin, 25);
  document.setFont('helvetica', 'normal');
  document.setFontSize(8);
  document.text(hasActiveFilters ? 'Vista semanal con filtros activos' : 'Vista general por horario', margin, 41);
  document.setFont('helvetica', 'bold');
  document.setFontSize(8.5);
  document.text(String(scopeLabel || '').toUpperCase(), pageWidth - margin, 23, {align: 'right'});
  document.setFont('helvetica', 'normal');
  document.setFontSize(7.5);
  document.text(`${formatWeekRange(weekValue)}  |  Página 1`, pageWidth - margin, 40, {align: 'right'});

  weekDays.forEach((day, dayIndex) => {
    const x = gridLeft + dayIndex * dayWidth;
    const eventCount = layoutsByDay[dayIndex].length;
    document.setFillColor(237, 243, 247);
    document.roundedRect(x + 2, dayHeaderTop, dayWidth - 4, 22, 4, 4, 'F');
    document.setTextColor(0, 40, 88);
    document.setFont('helvetica', 'bold');
    document.setFontSize(8);
    document.text(format(day, 'EEEE d', {locale: es}).toUpperCase(), x + dayWidth / 2, dayHeaderTop + 9, {align: 'center'});
    document.setFont('helvetica', 'normal');
    document.setFontSize(6.5);
    document.text(`${eventCount} ${eventCount === 1 ? 'actividad' : 'actividades'}`, x + dayWidth / 2, dayHeaderTop + 17, {align: 'center'});
  });

  document.setDrawColor(207, 224, 232);
  document.setLineWidth(0.5);
  for (let hour = startHour; hour <= endHour; hour += 1) {
    const y = gridTop + ((hour - startHour) * 60 / totalMinutes) * gridHeight;
    document.line(gridLeft, y, gridRight, y);
    if (hour < endHour) {
      document.setTextColor(80, 105, 125);
      document.setFont('helvetica', 'normal');
      document.setFontSize(6.5);
      document.text(`${String(hour).padStart(2, '0')}:00`, gridLeft - 6, y + 2, {align: 'right'});
    }
  }
  for (let dayIndex = 0; dayIndex <= weekDays.length; dayIndex += 1) {
    const x = gridLeft + dayIndex * dayWidth;
    document.line(x, gridTop, x, gridBottom);
  }

  layoutsByDay.forEach((dayLayout, dayIndex) => {
    dayLayout.forEach(positionedEvent => {
      const {event, startMinutes, endMinutes, lane, laneCount} = positionedEvent;
      const mediatorKey = String(event?.mediatorId || event?.mediatorName || 'sin-mediador');
      const color = mediatorMap.get(mediatorKey)?.color || WEEKLY_PDF_COLORS[0];
      const laneWidth = (dayWidth - 6) / laneCount;
      const x = gridLeft + dayIndex * dayWidth + 3 + lane * laneWidth;
      const y = gridTop + ((startMinutes - startHour * 60) / totalMinutes) * gridHeight;
      const naturalHeight = ((endMinutes - startMinutes) / totalMinutes) * gridHeight;
      const blockHeight = Math.max(naturalHeight - 2, 10);
      const blockWidth = Math.max(laneWidth - 2, 8);

      document.setFillColor(...color.fill);
      document.setDrawColor(...color.border);
      document.setLineWidth(1.1);
      document.roundedRect(x, y + 1, blockWidth, blockHeight, 2, 2, 'FD');

      const eventStart = getEventDate(event);
      const eventEnd = getEventEndDate(event);
      const eventText = [
        `${eventStart ? format(eventStart, 'HH:mm') : '--:--'}-${eventEnd ? format(eventEnd, 'HH:mm') : '--:--'}`,
        event.schoolName || '',
        getEncounterLabel(event),
        event.mediatorName || ''
      ].filter(Boolean).join('\n');
      document.setTextColor(0, 40, 88);
      document.setFont('helvetica', 'bold');
      document.setFontSize(5.8);
      const lines = document.splitTextToSize(eventText, Math.max(blockWidth - 6, 4));
      const maximumLines = Math.max(1, Math.floor((blockHeight - 4) / 6.2));
      document.text(lines.slice(0, maximumLines), x + 3, y + 7, {lineHeightFactor: 1.05});
    });
  });

  if (mediators.length > 0) {
    const legendTop = gridBottom + 13;
    const legendWidth = (gridRight - margin) / legendColumns;
    mediators.forEach((mediator, index) => {
      const column = index % legendColumns;
      const row = Math.floor(index / legendColumns);
      const x = margin + column * legendWidth;
      const y = legendTop + row * 14;
      document.setFillColor(...mediator.color.fill);
      document.setDrawColor(...mediator.color.border);
      document.roundedRect(x, y - 7, 8, 8, 2, 2, 'FD');
      document.setTextColor(0, 40, 88);
      document.setFont('helvetica', 'normal');
      document.setFontSize(6.5);
      const name = String(mediator.name);
      document.text(name.length > 38 ? `${name.slice(0, 35)}...` : name, x + 12, y);
    });
  }

  document.save(
    `agenda_semanal_${normalizeFilePart(scopeLabel)}_${format(weekDays[0], 'yyyy-MM-dd')}_${format(weekDays[6], 'yyyy-MM-dd')}.pdf`
  );
}

function buildInitialForm(date) {
  return {
    eventDate: toInputDate(date),
    eventTime: '',
    eventEndTime: '',
    schoolId: '',
    groupIds: [],
    mediatorId: '',
    locationAddress: '',
    encounterType: '',
    status: 'programado',
    details: ''
  };
}

function buildEditForm(event) {
  const eventDate = getEventDate(event);
  const eventEndDate = getEventEndDate(event);

  return {
    eventDate: toInputDate(eventDate),
    eventTime: toInputTime(eventDate),
    eventEndTime: toInputTime(eventEndDate),
    details: event?.details || ''
  };
}

function CalendarPage() {
  const [searchParams] = useSearchParams();
  const userId = localStorage.getItem(constants.USER_ID);
  const userRole = localStorage.getItem(constants.ROLE);
  const adminScope = normalizeAdminScope(searchParams.get('scope'));
  const isAdmin = isAdminRole(userRole);
  const isReviewer = isReviewerRole(userRole);
  const hasGeneralCalendarAccess = isAdmin || isReviewer;
  const activeScope = isReviewer ? 'general' : isAdmin ? adminScope : getScopeFromRole(userRole);
  const today = useMemo(() => new Date(), []);
  const [visibleMonth, setVisibleMonth] = useState(startOfMonth(today));
  const [selectedDate, setSelectedDate] = useState(today);
  const [schools, setSchools] = useState([]);
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openPdfDownloadModal, setOpenPdfDownloadModal] = useState(false);
  const [pdfDownloadType, setPdfDownloadType] = useState('monthly');
  const [pdfWeekDate, setPdfWeekDate] = useState(() => toInputDate(today));
  const [creatingEvent, setCreatingEvent] = useState(false);
  const [activeEventId, setActiveEventId] = useState(null);
  const [eventModalMode, setEventModalMode] = useState(null);
  const [savingEvent, setSavingEvent] = useState(false);
  const [eventForm, setEventForm] = useState(() => buildInitialForm(today));
  const [editForm, setEditForm] = useState(() => buildEditForm(null));
  const [statusForm, setStatusForm] = useState({ status: 'programado' });
  const [filters, setFilters] = useState({
    schoolId: [],
    entityType: [],
    mediatorId: [],
    encounterType: [],
    dateFrom: '',
    dateTo: '',
    status: []
  });
  const scopeLabel = getScopeLabel(activeScope);
  const canManageCalendarEvents = !isReviewer;
  const encounterOptions = useMemo(() => getEncounterOptions(activeScope), [activeScope]);
  const eventQueryFilters = useMemo(() => (
    hasGeneralCalendarAccess && activeScope && activeScope !== 'general'
      ? { scope: activeScope }
      : {}
  ), [activeScope, hasGeneralCalendarAccess]);

  useEffect(() => {
    let isMounted = true;

    async function loadSchools() {
      if (!hasGeneralCalendarAccess && (!userId || userId === 'null')) {
        setSchools([]);
        return;
      }

      const response = await getSchools(hasGeneralCalendarAccess ? undefined : Number(userId));
      const schoolsList = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : [];
      const scopedSchools = hasGeneralCalendarAccess && activeScope
        ? buildScopedSchools(schoolsList, activeScope)
        : schoolsList;

      if (!isMounted) return;
      setSchools(scopedSchools);
    }

    loadSchools();

    return () => {
      isMounted = false;
    };
  }, [activeScope, hasGeneralCalendarAccess, userId]);

  useEffect(() => {
    let isMounted = true;

    async function loadEvents() {
      setLoadingEvents(true);
      const eventsList = activeScope === 'general'
        ? (
            await Promise.all([
              getCalendarEvents({ scope: 'pf' }),
              getCalendarEvents({ scope: 'ndn' })
            ])
          ).flat()
        : await getCalendarEvents(eventQueryFilters);
      if (!isMounted) return;
      setEvents(eventsList);
      setLoadingEvents(false);
    }

    loadEvents();

    return () => {
      isMounted = false;
    };
  }, [activeScope, eventQueryFilters]);

  const selectedSchool = useMemo(
    () => schools.find((school) => String(school.id) === String(eventForm.schoolId)) || null,
    [eventForm.schoolId, schools]
  );
  const eventSchoolOptions = useMemo(
    () => isAdmin
      ? schools.filter(school => schoolBelongsToMediator(school, eventForm.mediatorId))
      : schools,
    [eventForm.mediatorId, isAdmin, schools]
  );
  const selectedSchoolGroups = useMemo(() => {
    const groups = Array.isArray(selectedSchool?.groups) ? selectedSchool.groups : [];
    const schoolHasSelectedMediator = String(getSchoolMediatorId(selectedSchool)) === String(eventForm.mediatorId);
    const availableGroups = isAdmin && eventForm.mediatorId && !schoolHasSelectedMediator
      ? groups.filter(group => String(getGroupMediatorId(group)) === String(eventForm.mediatorId))
      : groups;

    return availableGroups
      .filter(group => group?.id)
      .sort((firstGroup, secondGroup) => (
        getGroupName(firstGroup).localeCompare(getGroupName(secondGroup), 'es')
      ));
  }, [eventForm.mediatorId, isAdmin, selectedSchool]);

  const activeEvent = useMemo(
    () => events.find((event) => String(event.id) === String(activeEventId)) || null,
    [activeEventId, events]
  );
  const mediatorOptions = useMemo(() => {
    const mediatorMap = new Map();

    events.forEach((event) => {
      const mediatorId = event?.mediatorId;
      if (!mediatorId) return;

      mediatorMap.set(String(mediatorId), {
        id: mediatorId,
        name: event?.mediatorName || `Mediador ${mediatorId}`
      });
    });

    schools.forEach((school) => {
      const mediatorId = getSchoolMediatorId(school);
      if (mediatorId && !mediatorMap.has(String(mediatorId))) {
        mediatorMap.set(String(mediatorId), {
          id: mediatorId,
          name: getSchoolMediatorName(school) || `Mediador ${mediatorId}`
        });
      }

      (school?.groups || []).forEach(group => {
        const groupMediatorId = group?.userId ?? group?.user_id ?? group?.user?.id;
        if (!groupMediatorId || mediatorMap.has(String(groupMediatorId))) return;
        mediatorMap.set(String(groupMediatorId), {
          id: groupMediatorId,
          name: group?.user?.fullName || group?.user?.full_name || `Mediador ${groupMediatorId}`
        });
      });
    });

    return Array.from(mediatorMap.values()).sort((firstMediator, secondMediator) => (
      String(firstMediator.name).localeCompare(String(secondMediator.name), 'es')
    ));
  }, [events, schools]);
  const schoolFilterOptions = useMemo(() => {
    const schoolsByExactName = new Map();
    schools.forEach((school) => {
      const exactName = getSchoolName(school);
      if (!schoolsByExactName.has(exactName)) {
        schoolsByExactName.set(exactName, {
          id: exactName,
          name: exactName
        });
      }
    });
    return Array.from(schoolsByExactName.values()).sort((firstSchool, secondSchool) =>
      firstSchool.name.localeCompare(secondSchool.name, 'es')
    );
  }, [schools]);

  const monthDays = useMemo(() => {
    const monthStart = startOfMonth(visibleMonth);
    const monthEnd = endOfMonth(visibleMonth);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [visibleMonth]);

  const filteredEvents = useMemo(() => {
    const startDate = normalizeDateInput(filters.dateFrom);
    const endDate = normalizeDateInput(filters.dateTo, 'end');

    return events
      .filter((event) => {
        const eventDate = getEventDate(event);
        if (!eventDate) return false;

        if (!matchesAnySelected(filters.schoolId, event.schoolName)) {
          return false;
        }

        if (filters.entityType.length > 0) {
          const eventSchool = schools.find(
            school => getSchoolName(school) === String(event.schoolName || '')
          );
          if (!filters.entityType.some(type => matchesEntityType(eventSchool, type))) return false;
        }

        if (!matchesAnySelected(filters.mediatorId, event.mediatorId)) {
          return false;
        }

        if (
          !matchesAnySelected(filters.encounterType, event.encounterType)
        ) {
          return false;
        }

        if (!matchesAnySelected(filters.status, event.status)) {
          return false;
        }

        if (startDate && eventDate < startDate) return false;
        if (endDate && eventDate > endDate) return false;

        return true;
      })
      .sort((firstEvent, secondEvent) => {
        const firstDate = getEventDate(firstEvent);
        const secondDate = getEventDate(secondEvent);
        return (firstDate?.getTime() || 0) - (secondDate?.getTime() || 0);
      });
  }, [events, filters, schools]);

  const selectedDayEvents = filteredEvents.filter((event) => {
    const eventDate = getEventDate(event);
    return eventDate ? isSameDay(eventDate, selectedDate) : false;
  });
  const hasActiveFilters = Object.values(filters).some((value) =>
    Array.isArray(value) ? value.length > 0 : Boolean(value)
  );
  const downloadableAgendaEvents = useMemo(() => getDownloadableAgendaEvents({
    events,
    filteredEvents,
    hasActiveFilters,
    visibleMonth
  }), [events, filteredEvents, hasActiveFilters, visibleMonth]);

  function updateFilter(name, value) {
    setFilters((currentFilters) => ({
      ...currentFilters,
      [name]: value
    }));
  }

  function clearFilters() {
    setFilters({
      schoolId: [],
      entityType: [],
      mediatorId: [],
      encounterType: [],
      dateFrom: '',
      dateTo: '',
      status: []
    });
  }

  function canDownloadAgenda() {
    if (downloadableAgendaEvents.length === 0) {
      toast('No hay eventos para descargar', { type: 'warning' });
      return false;
    }

    return true;
  }

  function downloadAgendaExcel() {
    if (!canDownloadAgenda()) return;

    const worksheet = XLSX.utils.json_to_sheet(
      buildAgendaRows(downloadableAgendaEvents, scopeLabel),
      { header: AGENDA_EXPORT_HEADERS }
    );
    worksheet['!cols'] = [
      { wch: 24 },
      { wch: 14 },
      { wch: 10 },
      { wch: 24 },
      { wch: 24 },
      { wch: 16 },
      { wch: 36 },
      { wch: 18 },
      { wch: 18 },
      { wch: 34 },
      { wch: 18 },
      { wch: 26 },
      { wch: 48 }
    ];

    const workbook = XLSX.utils.book_new();
    const fileName = `${buildAgendaFileBaseName(
      scopeLabel,
      hasActiveFilters,
      visibleMonth
    )}.xlsx`;

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Agenda');
    downloadWorkbook(workbook, fileName);
  }

  function downloadMonthlyAgendaPdf() {
    if (!canDownloadAgenda()) return;

    const rows = buildAgendaRows(downloadableAgendaEvents, scopeLabel);
    const document = new JsPDF({
      orientation: 'landscape',
      unit: 'pt',
      format: 'legal'
    });
    const generatedAt = format(new Date(), "dd/MM/yyyy 'a las' HH:mm");

    document.autoTable({
      head: [AGENDA_EXPORT_HEADERS],
      body: rows.map(row => AGENDA_EXPORT_HEADERS.map(header => row[header] ?? '')),
      startY: 58,
      margin: { top: 58, right: 24, bottom: 32, left: 24 },
      theme: 'grid',
      styles: {
        font: 'helvetica',
        fontSize: 6.5,
        cellPadding: 3,
        overflow: 'linebreak',
        valign: 'middle',
        textColor: [20, 48, 73],
        lineColor: [207, 224, 232],
        lineWidth: 0.4
      },
      headStyles: {
        fillColor: [0, 107, 143],
        textColor: [255, 255, 255],
        fontStyle: 'bold'
      },
      alternateRowStyles: {
        fillColor: [244, 250, 252]
      },
      columnStyles: {
        0: { cellWidth: 65 },
        1: { cellWidth: 44 },
        2: { cellWidth: 35 },
        3: { cellWidth: 46 },
        4: { cellWidth: 72 },
        5: { cellWidth: 48 },
        6: { cellWidth: 100 },
        7: { cellWidth: 55 },
        8: { cellWidth: 55 },
        9: { cellWidth: 82 },
        10: { cellWidth: 54 },
        11: { cellWidth: 72 },
        12: { cellWidth: 'auto' }
      },
      didDrawPage: data => {
        document.setTextColor(0, 40, 88);
        document.setFont('helvetica', 'bold');
        document.setFontSize(13);
        document.text(`Agenda del calendario - ${scopeLabel}`, 24, 25);
        document.setFont('helvetica', 'normal');
        document.setFontSize(7.5);
        document.setTextColor(80, 105, 125);
        document.text(`Generado el ${generatedAt}`, 24, 40);
        document.text(
          `Página ${data.pageNumber}`,
          document.internal.pageSize.getWidth() - 24,
          document.internal.pageSize.getHeight() - 14,
          { align: 'right' }
        );
      }
    });

    document.save(`${buildAgendaFileBaseName(
      scopeLabel,
      hasActiveFilters,
      visibleMonth
    )}.pdf`);
  }

  function openPdfModal() {
    const initialWeekDate = isSameMonth(selectedDate, visibleMonth)
      ? selectedDate
      : visibleMonth;
    setPdfDownloadType('monthly');
    setPdfWeekDate(toInputDate(initialWeekDate));
    setOpenPdfDownloadModal(true);
  }

  function closePdfModal() {
    setOpenPdfDownloadModal(false);
  }

  function submitPdfDownload(event) {
    event.preventDefault();

    if (pdfDownloadType === 'monthly') {
      if (!canDownloadAgenda()) return;
      downloadMonthlyAgendaPdf();
      closePdfModal();
      return;
    }

    if (!pdfWeekDate) {
      toast('Selecciona la semana que deseas descargar', {type: 'warning'});
      return;
    }

    const weeklySourceEvents = hasActiveFilters ? filteredEvents : events;
    const weeklyEvents = getWeeklyAgendaEvents(weeklySourceEvents, pdfWeekDate);
    if (weeklyEvents.length === 0) {
      toast('No hay eventos para descargar en la semana seleccionada', {type: 'warning'});
      return;
    }

    createWeeklyAgendaPdf(weeklyEvents, pdfWeekDate, scopeLabel, hasActiveFilters);
    closePdfModal();
  }

  function updateEventForm(name, value) {
    if (name === 'mediatorId') {
      setEventForm((currentForm) => ({
        ...currentForm,
        mediatorId: value,
        schoolId: '',
        groupIds: [],
        locationAddress: ''
      }));
      return;
    }
    if (name === 'schoolId') {
      const school = schools.find((item) => String(item.id) === String(value));
      setEventForm((currentForm) => ({
        ...currentForm,
        schoolId: value,
        groupIds: [],
        locationAddress: getSchoolAddress(school)
      }));
      return;
    }

    setEventForm((currentForm) => ({
      ...currentForm,
      [name]: value
    }));
  }

  function selectEventGroup(groupId) {
    setEventForm(currentForm => ({
      ...currentForm,
      groupIds: groupId ? [String(groupId)] : []
    }));
  }

  function updateEditForm(name, value) {
    setEditForm((currentForm) => ({
      ...currentForm,
      [name]: value
    }));
  }

  function updateStatusForm(name, value) {
    setStatusForm((currentForm) => ({
      ...currentForm,
      [name]: value
    }));
  }

  function openNewEventModal() {
    if (!canManageCalendarEvents) return;
    setEventForm(buildInitialForm(selectedDate));
    setOpenCreateModal(true);
  }

  function closeNewEventModal() {
    if (creatingEvent) return;
    setOpenCreateModal(false);
  }

  function openEventDetails(event) {
    setActiveEventId(event.id);
    setEventModalMode('detail');
  }

  function closeEventModal() {
    if (savingEvent) return;
    setEventModalMode(null);
    setActiveEventId(null);
  }

  function openEditEvent(event = activeEvent) {
    if (!event) return;
    setActiveEventId(event.id);
    setEditForm(buildEditForm(event));
    setEventModalMode('edit');
  }

  function openStatusEvent(event = activeEvent) {
    if (!event) return;
    setActiveEventId(event.id);
    setStatusForm({ status: event.status || 'programado' });
    setEventModalMode('status');
  }

  function returnToEventDetails() {
    if (savingEvent) return;
    setEventModalMode(null);
    setActiveEventId(null);
  }

  function updateEventInList(updatedEvent) {
    if (!updatedEvent?.id) return;

    setEvents((currentEvents) => currentEvents.map((event) => (
      String(event.id) === String(updatedEvent.id) ? updatedEvent : event
    )));
  }

  async function reloadEvents() {
    setLoadingEvents(true);
    const eventsList = activeScope === 'general'
      ? (
          await Promise.all([
            getCalendarEvents({ scope: 'pf' }),
            getCalendarEvents({ scope: 'ndn' })
          ])
        ).flat()
      : await getCalendarEvents(eventQueryFilters);
    setEvents(eventsList);
    setLoadingEvents(false);
  }

  async function submitEvent(event) {
    event.preventDefault();
    if (creatingEvent || !canManageCalendarEvents) return;

    const school = schools.find((item) => String(item.id) === String(eventForm.schoolId));
    const municipalityId = getSchoolMunicipalityId(school);

    if (isAdmin && !eventForm.mediatorId) {
      toast('Selecciona un mediador', { type: 'warning' });
      return;
    }

    if (!eventForm.eventDate) {
      toast('Selecciona una fecha', { type: 'warning' });
      return;
    }

    if (isTodayOrPastInBogota(eventForm.eventDate)) {
      toast('No se pueden cargar eventos para hoy ni para fechas pasadas', { type: 'warning' });
      return;
    }

    if (!eventForm.eventTime) {
      toast('Selecciona una hora', { type: 'warning' });
      return;
    }

    if (!eventForm.eventEndTime) {
      toast('Selecciona una hora estimada de finalización', { type: 'warning' });
      return;
    }

    if (eventForm.eventEndTime <= eventForm.eventTime) {
      toast('La hora estimada de finalización debe ser posterior a la hora de inicio', {
        type: 'warning'
      });
      return;
    }

    if (!school) {
      toast('Selecciona una Entidad Aliada', { type: 'warning' });
      return;
    }

    if (!municipalityId) {
      toast('La Entidad Aliada no tiene comuna asociada', { type: 'warning' });
      return;
    }

    if (!String(eventForm.locationAddress || '').trim()) {
      toast('Digite la direccion del evento', { type: 'warning' });
      return;
    }

    if (!eventForm.encounterType) {
      toast('Selecciona el tipo de encuentro', { type: 'warning' });
      return;
    }

    const groupValidationMessage = getEncounterGroupValidationMessage(
      eventForm.encounterType,
      selectedSchoolGroups,
      eventForm.groupIds
    );
    if (groupValidationMessage) {
      toast(groupValidationMessage, { type: 'warning' });
      return;
    }

    if (!confirmShortNoticeSchedule(eventForm.eventDate)) return;

    const selectedGroup = selectedSchoolGroups.find(group => (
      eventForm.groupIds.map(String).includes(String(group.id))
    ));
    const details = selectedGroup
      ? appendGroupToDetails(eventForm.details, getGroupName(selectedGroup))
      : String(eventForm.details || '').trim();

    setCreatingEvent(true);
    const response = await createCalendarEvent({
      schoolId: Number(school.id),
      groupIds: eventForm.groupIds.map(Number),
      mediatorId: isAdmin ? Number(eventForm.mediatorId) : undefined,
      municipalityId: Number(municipalityId),
      locationAddress: String(eventForm.locationAddress || '').trim(),
      encounterType: eventForm.encounterType,
      details,
      status: eventForm.status,
      startsAt: `${eventForm.eventDate}T${eventForm.eventTime}:00${COLOMBIA_OFFSET}`,
      endsAt: `${eventForm.eventDate}T${eventForm.eventEndTime}:00${COLOMBIA_OFFSET}`
    });
    setCreatingEvent(false);

    if (!response?.success) return;

    toast('Evento creado exitosamente', { type: 'success' });
    setOpenCreateModal(false);
    setSelectedDate(new Date(`${eventForm.eventDate}T00:00:00`));
    setVisibleMonth(startOfMonth(new Date(`${eventForm.eventDate}T00:00:00`)));
    await reloadEvents();
  }

  async function submitEditEvent(event) {
    event.preventDefault();
    if (savingEvent || !activeEvent) return;

    if (!editForm.eventDate) {
      toast('Selecciona una fecha', { type: 'warning' });
      return;
    }

    if (isTodayOrPastInBogota(editForm.eventDate)) {
      toast('No se pueden programar eventos para hoy ni para fechas pasadas', { type: 'warning' });
      return;
    }

    if (!editForm.eventTime) {
      toast('Selecciona una hora', { type: 'warning' });
      return;
    }

    if (!editForm.eventEndTime) {
      toast('Selecciona una hora estimada de finalización', { type: 'warning' });
      return;
    }

    if (editForm.eventEndTime <= editForm.eventTime) {
      toast('La hora estimada de finalización debe ser posterior a la hora de inicio', {
        type: 'warning'
      });
      return;
    }

    if (!confirmShortNoticeSchedule(editForm.eventDate)) return;

    setSavingEvent(true);
    const response = await updateCalendarEvent(activeEvent.id, {
      startsAt: `${editForm.eventDate}T${editForm.eventTime}:00${COLOMBIA_OFFSET}`,
      endsAt: `${editForm.eventDate}T${editForm.eventEndTime}:00${COLOMBIA_OFFSET}`,
      details: String(editForm.details || '').trim()
    });
    setSavingEvent(false);

    if (!response?.success) return;

    updateEventInList(response.data);
    const updatedDate = getEventDate(response.data);
    if (updatedDate) {
      setSelectedDate(updatedDate);
      setVisibleMonth(startOfMonth(updatedDate));
    }
    toast('Evento actualizado exitosamente', { type: 'success' });
    setEventModalMode(null);
    setActiveEventId(null);
  }

  async function submitStatusEvent(event) {
    event.preventDefault();
    if (savingEvent || !activeEvent) return;

    if (!statusForm.status) {
      toast('Selecciona un estado', { type: 'warning' });
      return;
    }

    setSavingEvent(true);
    const response = await updateCalendarEvent(activeEvent.id, {
      status: statusForm.status
    });
    setSavingEvent(false);

    if (!response?.success) return;

    updateEventInList(response.data);
    toast('Estado actualizado exitosamente', { type: 'success' });
    setEventModalMode(null);
    setActiveEventId(null);
  }

  return (
    <section className='calendar-page'>
      <div className='calendar-page__content'>
        <header className='calendar-page__header'>
          <div>
            <h1 className='calendar-page__title'>Calendario</h1>
            <span className='calendar-page__scope'>{scopeLabel}</span>
          </div>

          <div className='calendar-page__actions'>
            <button
              type='button'
              className='calendar-page__download'
              onClick={downloadAgendaExcel}
              disabled={loadingEvents}
            >
              <span className='material-symbols-rounded' aria-hidden='true'>
                table_view
              </span>
              Descargar Excel
            </button>

            <button
              type='button'
              className='calendar-page__download'
              onClick={openPdfModal}
              disabled={loadingEvents}
            >
              <span className='material-symbols-rounded' aria-hidden='true'>
                picture_as_pdf
              </span>
              Descargar PDF
            </button>

            {canManageCalendarEvents && (
              <button
                type='button'
                className='calendar-page__create'
                onClick={openNewEventModal}
              >
                <span className='material-symbols-rounded' aria-hidden='true'>
                  add
                </span>
                Nuevo evento
              </button>
            )}
          </div>
        </header>

        <section className='calendar-filters' aria-label='Filtros del calendario'>
          {isAdmin && (
            <AdminProcessScopeFilter
              variant='calendar'
              onScopeChange={clearFilters}
            />
          )}
          <div className='calendar-filters__field'>
            <span>Tipo de entidad</span>
            <CustomFilterSelect
              optionsList={ENTITY_TYPE_FILTER_OPTIONS}
              placeholder='Todos los tipos de entidad'
              valueEdit={getMultiFilterLabel(ENTITY_TYPE_FILTER_OPTIONS, filters.entityType)}
              addInfo={(_, values) => updateFilter('entityType', normalizeMultiSelection(values))}
              multiple
              selectedValues={filters.entityType}
            />
          </div>

          <div className='calendar-filters__field'>
            <span>Entidad Aliada</span>
            <CustomFilterSelect
              optionsList={schoolFilterOptions}
              placeholder='Todas'
              valueEdit={getMultiFilterLabel(schoolFilterOptions, filters.schoolId)}
              addInfo={(_, values) => updateFilter('schoolId', normalizeMultiSelection(values))}
              multiple
              selectedValues={filters.schoolId}
              searchable
            />
          </div>

          {hasGeneralCalendarAccess && (
            <div className='calendar-filters__field'>
              <span>Mediador</span>
              <CustomFilterSelect
                optionsList={mediatorOptions}
                placeholder='Todos'
                valueEdit={getMultiFilterLabel(mediatorOptions, filters.mediatorId)}
                addInfo={(_, values) => updateFilter('mediatorId', normalizeMultiSelection(values))}
                multiple
                selectedValues={filters.mediatorId}
                searchable
              />
            </div>
          )}

          {isAdmin && (
            <div className='calendar-filters__field'>
              <span>Tipo de encuentro</span>
              <CustomFilterSelect
                optionsList={encounterOptions.map(option => ({ id: option.value, name: option.label }))}
                placeholder='Todos'
                valueEdit={getMultiFilterLabel(encounterOptions.map(option => ({ id: option.value, name: option.label })), filters.encounterType)}
                addInfo={(_, values) => updateFilter('encounterType', normalizeMultiSelection(values))}
                multiple
                selectedValues={filters.encounterType}
              />
            </div>
          )}

          <label className='calendar-filters__field'>
            <span>Desde</span>
            <input
              type='date'
              value={filters.dateFrom}
              onChange={(event) => updateFilter('dateFrom', event.target.value)}
            />
          </label>

          <label className='calendar-filters__field'>
            <span>Hasta</span>
            <input
              type='date'
              value={filters.dateTo}
              onChange={(event) => updateFilter('dateTo', event.target.value)}
            />
          </label>

          <div className='calendar-filters__field'>
            <span>Estado</span>
            <CustomFilterSelect
              optionsList={STATUS_OPTIONS.map(status => ({ id: status.value, name: status.label }))}
              placeholder='Todos'
              valueEdit={getMultiFilterLabel(STATUS_OPTIONS.map(status => ({ id: status.value, name: status.label })), filters.status)}
              addInfo={(_, values) => updateFilter('status', normalizeMultiSelection(values))}
              multiple
              selectedValues={filters.status}
            />
          </div>

          <button
            type='button'
            className='calendar-filters__clear'
            onClick={clearFilters}
            disabled={!hasActiveFilters}
          >
            <span className='material-symbols-rounded' aria-hidden='true'>
              filter_alt_off
            </span>
            Limpiar filtros
          </button>
        </section>

        <section className='calendar-layout'>
          <div className='calendar-board'>
            <div className='calendar-board__toolbar'>
              <button
                type='button'
                className='calendar-board__nav'
                onClick={() => setVisibleMonth((currentMonth) => subMonths(currentMonth, 1))}
                aria-label='Mes anterior'
              >
                <span className='material-symbols-rounded' aria-hidden='true'>
                  chevron_left
                </span>
              </button>

              <h2 className='calendar-board__month'>
                {format(visibleMonth, 'MMMM yyyy', { locale: es })}
              </h2>

              <button
                type='button'
                className='calendar-board__nav'
                onClick={() => setVisibleMonth((currentMonth) => addMonths(currentMonth, 1))}
                aria-label='Mes siguiente'
              >
                <span className='material-symbols-rounded' aria-hidden='true'>
                  chevron_right
                </span>
              </button>
            </div>

            <div className='calendar-board__weekdays'>
              {WEEK_DAYS.map((day) => (
                <span key={day}>{day}</span>
              ))}
            </div>

            <div className='calendar-board__days'>
              {monthDays.map((day) => {
                const dayEvents = filteredEvents.filter((event) => {
                  const eventDate = getEventDate(event);
                  return eventDate ? isSameDay(eventDate, day) : false;
                });
                const isSelected = isSameDay(day, selectedDate);
                const isToday = isSameDay(day, today);
                const belongsToMonth = isSameMonth(day, visibleMonth);

                return (
                  <button
                    key={day.toISOString()}
                    type='button'
                    className={[
                      'calendar-day',
                      isSelected ? 'calendar-day--selected' : '',
                      isToday ? 'calendar-day--today' : '',
                      belongsToMonth ? '' : 'calendar-day--muted'
                    ].filter(Boolean).join(' ')}
                    onClick={() => setSelectedDate(day)}
                  >
                    <span className='calendar-day__number'>{format(day, 'd')}</span>
                    {dayEvents.length > 0 && (
                      <span className='calendar-day__count'>{dayEvents.length}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <aside className='calendar-agenda'>
            <div className='calendar-agenda__header'>
              <span className='calendar-agenda__date'>
                {format(selectedDate, "EEEE d 'de' MMMM", { locale: es })}
              </span>
              <span className='calendar-agenda__total'>
                {selectedDayEvents.length} eventos
              </span>
            </div>

            {loadingEvents ? (
              <div className='calendar-agenda__empty'>
                <span className='material-symbols-rounded' aria-hidden='true'>
                  progress_activity
                </span>
                <p>Cargando eventos...</p>
              </div>
            ) : selectedDayEvents.length > 0 ? (
              <div className='calendar-agenda__list'>
                {selectedDayEvents.map((event) => {
                  return (
                    <article className='calendar-event' key={event.id}>
                      <div className='calendar-event__top'>
                        <span className='calendar-event__time'>
                          {getEventTimeLabel(event)} - {getEventEndTimeLabel(event)}
                        </span>
                        <span className={`calendar-status calendar-status--${event.status || 'programado'}`}>
                          {getStatusLabel(event.status)}
                        </span>
                      </div>
                      <strong className='calendar-event__title'>
                        {getEncounterLabel(event)}
                      </strong>
                      {event.mediatorName ? (
                        <span className='calendar-event__mediator'>
                          Mediador: {event.mediatorName}
                        </span>
                      ) : null}
                      <span className='calendar-event__place'>{event.schoolName}</span>
                      <span className='calendar-event__address'>{event.locationAddress}</span>
                      {event.details ? (
                        <p className='calendar-event__details calendar-event__details--preview' title={event.details}>
                          {event.details}
                        </p>
                      ) : null}
                      <div className='calendar-event__actions'>
                        <button
                          type='button'
                          className='calendar-event__action'
                          onClick={() => openEventDetails(event)}
                        >
                          Ver
                        </button>
                        {canManageCalendarEvents && (
                          <>
                            <button
                              type='button'
                              className='calendar-event__action'
                              onClick={() => openEditEvent(event)}
                            >
                              Editar
                            </button>
                            <button
                              type='button'
                              className='calendar-event__action'
                              onClick={() => openStatusEvent(event)}
                            >
                              Estado
                            </button>
                          </>
                        )}
                      </div>
                      {isScheduledWithShortNotice(event) ? (
                        <span
                          className='calendar-event__short-notice'
                          title='Evento cargado con menos de 2 días de anticipación'
                          aria-label='Evento cargado con menos de 2 días de anticipación'
                        >
                          <span className='material-symbols-rounded' aria-hidden='true'>warning</span>
                        </span>
                      ) : null}
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className='calendar-agenda__empty'>
                <span className='material-symbols-rounded' aria-hidden='true'>
                  event_busy
                </span>
                <p>No hay eventos registrados para {toInputDate(selectedDate)}.</p>
              </div>
            )}
          </aside>
        </section>
      </div>

      {openPdfDownloadModal && (
        <ModalContainer>
          <section
            className='calendar-event-modal calendar-event-modal--pdf'
            role='dialog'
            aria-modal='true'
            aria-labelledby='calendar-pdf-modal-title'
          >
            <div className='calendar-event-modal__header'>
              <h3 id='calendar-pdf-modal-title'>Descargar PDF</h3>
              <button
                type='button'
                className='calendar-event-modal__close'
                onClick={closePdfModal}
                aria-label='Cerrar'
              >
                <span className='material-symbols-rounded' aria-hidden='true'>close</span>
              </button>
            </div>

            <form className='calendar-event-modal__form' onSubmit={submitPdfDownload}>
              <div className='calendar-pdf-options' role='radiogroup' aria-label='Tipo de PDF'>
                <label className={`calendar-pdf-option ${pdfDownloadType === 'monthly' ? 'calendar-pdf-option--selected' : ''}`}>
                  <input
                    type='radio'
                    name='pdfDownloadType'
                    value='monthly'
                    checked={pdfDownloadType === 'monthly'}
                    onChange={(event) => setPdfDownloadType(event.target.value)}
                  />
                  <span className='material-symbols-rounded' aria-hidden='true'>calendar_month</span>
                  <strong>PDF mensual</strong>
                  <small>
                    {hasActiveFilters
                      ? 'Descarga el resultado completo de los filtros activos.'
                      : `Descarga los eventos de ${format(visibleMonth, 'MMMM yyyy', {locale: es})}.`}
                  </small>
                </label>

                <label className={`calendar-pdf-option ${pdfDownloadType === 'weekly' ? 'calendar-pdf-option--selected' : ''}`}>
                  <input
                    type='radio'
                    name='pdfDownloadType'
                    value='weekly'
                    checked={pdfDownloadType === 'weekly'}
                    onChange={(event) => setPdfDownloadType(event.target.value)}
                  />
                  <span className='material-symbols-rounded' aria-hidden='true'>view_week</span>
                  <strong>PDF semanal</strong>
                  <small>Genera una agenda semanal por horarios, similar a Google Calendar.</small>
                </label>
              </div>

              {pdfDownloadType === 'weekly' && (
                <label className='calendar-event-modal__field'>
                  <span>Semana a descargar</span>
                  <input
                    type='date'
                    required
                    value={pdfWeekDate}
                    onChange={(event) => setPdfWeekDate(event.target.value)}
                  />
                  {pdfWeekDate && (
                    <small className='calendar-pdf-week-range'>
                      Se descargará del {formatWeekRange(pdfWeekDate)}.
                      {hasActiveFilters ? ' Se respetarán los filtros activos.' : ''}
                    </small>
                  )}
                </label>
              )}

              <div className='calendar-event-modal__actions'>
                <button
                  type='button'
                  className='button-white-border-blue'
                  onClick={closePdfModal}
                >
                  Cancelar
                </button>
                <button type='submit' className='blue-site-button'>
                  Descargar
                </button>
              </div>
            </form>
          </section>
        </ModalContainer>
      )}

      {canManageCalendarEvents && openCreateModal && (
        <ModalContainer>
          <section
            className='calendar-event-modal'
            role='dialog'
            aria-modal='true'
            aria-labelledby='calendar-event-modal-title'
          >
            <div className='calendar-event-modal__header'>
              <h3 id='calendar-event-modal-title'>Nuevo evento</h3>
              <button
                type='button'
                className='calendar-event-modal__close'
                onClick={closeNewEventModal}
                aria-label='Cerrar'
              >
                <span className='material-symbols-rounded' aria-hidden='true'>
                  close
                </span>
              </button>
            </div>

            <form className='calendar-event-modal__form' onSubmit={submitEvent}>
              <div className='calendar-event-modal__row calendar-event-modal__row--three'>
                <label className='calendar-event-modal__field'>
                  <span>Fecha</span>
                  <input
                    type='date'
                    min={getMinimumScheduleDate()}
                    value={eventForm.eventDate}
                    onChange={(event) => updateEventForm('eventDate', event.target.value)}
                  />
                </label>

                <label className='calendar-event-modal__field'>
                  <span>Hora de inicio</span>
                  <input
                    type='time'
                    value={eventForm.eventTime}
                    onChange={(event) => updateEventForm('eventTime', event.target.value)}
                  />
                </label>

                <label className='calendar-event-modal__field'>
                  <span>Hora estimada de finalización</span>
                  <input
                    type='time'
                    value={eventForm.eventEndTime}
                    onChange={(event) => updateEventForm('eventEndTime', event.target.value)}
                  />
                </label>
              </div>

              {isAdmin && (
                <label className='calendar-event-modal__field'>
                  <span>Mediador</span>
                  <select
                    value={eventForm.mediatorId}
                    onChange={(event) => updateEventForm('mediatorId', event.target.value)}>
                    <option value=''>Seleccione</option>
                    {mediatorOptions.map((mediator) => (
                      <option key={mediator.id} value={mediator.id}>
                        {mediator.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              <label className='calendar-event-modal__field'>
                <span>Entidad Aliada</span>
                <select
                  value={eventForm.schoolId}
                  onChange={(event) => updateEventForm('schoolId', event.target.value)}
                  disabled={isAdmin ? !eventForm.mediatorId || eventSchoolOptions.length === 0 : schools.length === 0}
                >
                  <option value=''>
                    {isAdmin && !eventForm.mediatorId
                      ? 'Seleccione primero un mediador'
                      : eventSchoolOptions.length === 0
                        ? 'Sin entidades aliadas disponibles'
                        : 'Seleccione'}
                  </option>
                  {eventSchoolOptions.map((school) => (
                    <option key={school.id} value={school.id}>
                      {getSchoolOptionLabel(school)}
                    </option>
                  ))}
                </select>
              </label>

              {selectedSchool && (
                <fieldset className='calendar-event-modal__group-selection'>
                  <legend>
                    Grupo al que asistirás
                    {requiresEncounterGroup(eventForm.encounterType) ? ' *' : ''}
                  </legend>
                  {selectedSchoolGroups.length > 0 ? (
                    <select
                      value={eventForm.groupIds[0] || ''}
                      onChange={(event) => selectEventGroup(event.target.value)}
                    >
                      <option value=''>Seleccione</option>
                      {selectedSchoolGroups.map(group => (
                        <option key={group.id} value={group.id}>
                          {getGroupName(group)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p className='calendar-event-modal__group-empty' role='status'>
                      {requiresEncounterGroup(eventForm.encounterType)
                        ? GROUPS_MUST_BE_CREATED_MESSAGE
                        : 'Esta Entidad Aliada no tiene grupos disponibles.'}
                    </p>
                  )}
                </fieldset>
              )}

              <label className='calendar-event-modal__field'>
                <span>Comuna</span>
                <input
                  type='text'
                  value={getSchoolMunicipalityName(selectedSchool)}
                  readOnly
                />
              </label>

              <label className='calendar-event-modal__field'>
                <span>Direccion</span>
                <input
                  type='text'
                  maxLength='255'
                  value={eventForm.locationAddress}
                  onChange={(event) => updateEventForm('locationAddress', event.target.value)}
                />
              </label>

              <div className='calendar-event-modal__row calendar-event-modal__row--two'>
                <label className='calendar-event-modal__field'>
                  <span># de encuentro</span>
                  <select
                    value={eventForm.encounterType}
                    onChange={(event) => updateEventForm('encounterType', event.target.value)}
                  >
                    <option value=''>Seleccione</option>
                    {encounterOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className='calendar-event-modal__field'>
                  <span>Estado</span>
                  <select
                    value={eventForm.status}
                    onChange={(event) => updateEventForm('status', event.target.value)}
                  >
                    {STATUS_OPTIONS.map((status) => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <label className='calendar-event-modal__field'>
                <span>Detalles</span>
                <textarea
                  rows='4'
                  value={eventForm.details}
                  onChange={(event) => updateEventForm('details', event.target.value)}
                />
              </label>

              <div className='calendar-event-modal__actions'>
                <button
                  type='button'
                  className='button-white-border-blue'
                  onClick={closeNewEventModal}
                  disabled={creatingEvent}
                >
                  Cancelar
                </button>
                <button
                  type='submit'
                  className='blue-site-button'
                  disabled={creatingEvent}
                >
                  {creatingEvent ? 'Guardando...' : 'Crear evento'}
                </button>
              </div>
            </form>
          </section>
        </ModalContainer>
      )}

      {eventModalMode === 'detail' && activeEvent && (
        <ModalContainer>
          <section
            className='calendar-event-modal calendar-event-modal--detail'
            role='dialog'
            aria-modal='true'
            aria-labelledby='calendar-event-detail-title'
          >
            <div className='calendar-event-modal__header'>
              <h3 id='calendar-event-detail-title'>Detalle del evento</h3>
              <button
                type='button'
                className='calendar-event-modal__close'
                onClick={closeEventModal}
                aria-label='Cerrar'
              >
                <span className='material-symbols-rounded' aria-hidden='true'>
                  close
                </span>
              </button>
            </div>

            <div className='calendar-event-modal__detail'>
              {isScheduledWithShortNotice(activeEvent) ? (
                <div className='calendar-event-modal__short-notice' role='status'>
                  <span className='material-symbols-rounded' aria-hidden='true'>warning</span>
                  Evento cargado con menos de 2 días de anticipación.
                </div>
              ) : null}
              <div className='calendar-event-modal__detail-main'>
                <span className={`calendar-status calendar-status--${activeEvent.status || 'programado'}`}>
                  {getStatusLabel(activeEvent.status)}
                </span>
                <strong>{getEncounterLabel(activeEvent)}</strong>
                <p>{activeEvent.schoolName}</p>
              </div>

              <dl className='calendar-event-modal__detail-grid'>
                <div>
                  <dt>Fecha</dt>
                  <dd>
                    {getEventDate(activeEvent)
                      ? format(getEventDate(activeEvent), "EEEE d 'de' MMMM 'de' yyyy", { locale: es })
                      : 'Sin fecha'}
                  </dd>
                </div>
                <div>
                  <dt>Hora de inicio</dt>
                  <dd>{getEventTimeLabel(activeEvent)}</dd>
                </div>
                <div>
                  <dt>Hora estimada de finalización</dt>
                  <dd>{getEventEndTimeLabel(activeEvent)}</dd>
                </div>
                <div>
                  <dt>Entidad Aliada</dt>
                  <dd>{activeEvent.schoolName || 'Sin informacion'}</dd>
                </div>
                <div>
                  <dt>Comuna</dt>
                  <dd>{activeEvent.municipalityName || 'Sin informacion'}</dd>
                </div>
                <div>
                  <dt>Direccion</dt>
                  <dd>{activeEvent.locationAddress || 'Sin direccion'}</dd>
                </div>
                <div>
                  <dt># de encuentro</dt>
                  <dd>{getEncounterLabel(activeEvent)}</dd>
                </div>
                {activeEvent.mediatorName ? (
                  <div>
                    <dt>Mediador</dt>
                    <dd>{activeEvent.mediatorName}</dd>
                  </div>
                ) : null}
                <div className='calendar-event-modal__created-at'>
                  <dt>Fecha de carga del evento</dt>
                  <dd>
                    {activeEvent.createdAt
                      ? `Este evento fue cargado el día ${formatBogotaDateTime(activeEvent.createdAt)}`
                      : 'Fecha de carga no disponible'}
                  </dd>
                </div>
              </dl>

              <div className='calendar-event-modal__detail-notes'>
                <span>Detalles</span>
                <p>{activeEvent.details || 'Sin detalles registrados.'}</p>
              </div>
            </div>

            {canManageCalendarEvents && (
              <div className='calendar-event-modal__actions'>
                <button
                  type='button'
                  className='button-white-border-blue'
                  onClick={() => openStatusEvent(activeEvent)}
                >
                  Cambiar estado
                </button>
                <button
                  type='button'
                  className='blue-site-button'
                  onClick={() => openEditEvent(activeEvent)}
                >
                  Editar
                </button>
              </div>
            )}
          </section>
        </ModalContainer>
      )}

      {canManageCalendarEvents && eventModalMode === 'edit' && activeEvent && (
        <ModalContainer>
          <section
            className='calendar-event-modal'
            role='dialog'
            aria-modal='true'
            aria-labelledby='calendar-event-edit-title'
          >
            <div className='calendar-event-modal__header'>
              <h3 id='calendar-event-edit-title'>Editar evento</h3>
              <button
                type='button'
                className='calendar-event-modal__close'
                onClick={closeEventModal}
                aria-label='Cerrar'
              >
                <span className='material-symbols-rounded' aria-hidden='true'>
                  close
                </span>
              </button>
            </div>

            <form className='calendar-event-modal__form' onSubmit={submitEditEvent}>
              <div className='calendar-event-modal__readonly-summary'>
                <strong>{getEncounterLabel(activeEvent)}</strong>
                <span>{activeEvent.schoolName}</span>
              </div>

              <div className='calendar-event-modal__row calendar-event-modal__row--three'>
                <label className='calendar-event-modal__field'>
                  <span>Fecha</span>
                  <input
                    type='date'
                    min={getMinimumScheduleDate()}
                    value={editForm.eventDate}
                    onChange={(event) => updateEditForm('eventDate', event.target.value)}
                  />
                </label>

                <label className='calendar-event-modal__field'>
                  <span>Hora de inicio</span>
                  <input
                    type='time'
                    value={editForm.eventTime}
                    onChange={(event) => updateEditForm('eventTime', event.target.value)}
                  />
                </label>

                <label className='calendar-event-modal__field'>
                  <span>Hora estimada de finalización</span>
                  <input
                    type='time'
                    value={editForm.eventEndTime}
                    onChange={(event) => updateEditForm('eventEndTime', event.target.value)}
                  />
                </label>
              </div>

              <label className='calendar-event-modal__field'>
                <span>Detalles</span>
                <textarea
                  rows='6'
                  value={editForm.details}
                  onChange={(event) => updateEditForm('details', event.target.value)}
                />
              </label>

              <div className='calendar-event-modal__actions'>
                <button
                  type='button'
                  className='button-white-border-blue'
                  onClick={returnToEventDetails}
                  disabled={savingEvent}
                >
                  Cancelar
                </button>
                <button
                  type='submit'
                  className='blue-site-button'
                  disabled={savingEvent}
                >
                  {savingEvent ? 'Guardando...' : 'Guardar cambios'}
                </button>
              </div>
            </form>
          </section>
        </ModalContainer>
      )}

      {canManageCalendarEvents && eventModalMode === 'status' && activeEvent && (
        <ModalContainer>
          <section
            className='calendar-event-modal calendar-event-modal--status'
            role='dialog'
            aria-modal='true'
            aria-labelledby='calendar-event-status-title'
          >
            <div className='calendar-event-modal__header'>
              <h3 id='calendar-event-status-title'>Cambiar estado</h3>
              <button
                type='button'
                className='calendar-event-modal__close'
                onClick={closeEventModal}
                aria-label='Cerrar'
              >
                <span className='material-symbols-rounded' aria-hidden='true'>
                  close
                </span>
              </button>
            </div>

            <form className='calendar-event-modal__form' onSubmit={submitStatusEvent}>
              <div className='calendar-event-modal__readonly-summary'>
                <strong>{getEncounterLabel(activeEvent)}</strong>
                <span>{activeEvent.schoolName}</span>
              </div>

              <label className='calendar-event-modal__field'>
                <span>Estado</span>
                <select
                  value={statusForm.status}
                  onChange={(event) => updateStatusForm('status', event.target.value)}
                >
                  {STATUS_OPTIONS.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </label>

              <div className='calendar-event-modal__actions'>
                <button
                  type='button'
                  className='button-white-border-blue'
                  onClick={returnToEventDetails}
                  disabled={savingEvent}
                >
                  Cancelar
                </button>
                <button
                  type='submit'
                  className='blue-site-button'
                  disabled={savingEvent}
                >
                  {savingEvent ? 'Guardando...' : 'Actualizar estado'}
                </button>
              </div>
            </form>
          </section>
        </ModalContainer>
      )}
    </section>
  );
}

export default CalendarPage;
